import React from "react";
import { Loader2, Search } from "lucide-react";
import { apiPriceList, formatApiErrorDetail } from "@/lib/api";
import { KOLOM_HARGA, saring, tanpaHarga, teksHarga } from "@/lib/priceList";
import useLayarKecil from "@/hooks/useLayarKecil";

// Owner 2026-10-10/11: read-only Price List — Enduser, Reseller and Member base
// prices with the unit; no quantity tiers; HPP never comes from the server.
// Used by the Price List page and the Kasir/CS home page. Table on a computer,
// cards on a phone.
const Harga = ({ b, kolom, besar = false }) => {
  const teks = teksHarga(b, kolom);
  if (!teks) return <span data-testid={`pl-${kolom}-${b.kode}`} className="text-xs italic text-slate-500">sesuai pesanan</span>;
  return (
    <span data-testid={`pl-${kolom}-${b.kode}`} className={besar ? "" : "whitespace-nowrap"}>
      <span className="font-semibold text-slate-100 tabular-nums">{teks}</span>
      {b.per && <span className="ml-0.5 text-xs text-slate-500">{b.per}</span>}
    </span>
  );
};

const SesuaiPesanan = ({ b }) => (
  <span data-testid={`pl-pesanan-${b.kode}`} className="italic text-slate-500">Harga sesuai pesanan</span>
);

const Kategori = ({ b }) => (
  <span className="rounded-md border border-amber-500/40 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{b.kategori}</span>
);

export default function PriceListPanel({ testId = "price-list-panel" }) {
  const kecil = useLayarKecil();
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [kat, setKat] = React.useState("");
  React.useEffect(() => {
    apiPriceList().then(setD).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  const rows = d ? saring(d.barang, q, kat) : [];

  return (
    <div data-testid={testId} className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <label className="relative flex-1 min-w-[14rem]">
          <span className="sr-only">Cari nama atau kode</span>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" aria-hidden />
          <input data-testid="pl-cari" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / kode barang"
            className="w-full min-h-[44px] rounded-lg border border-slate-700 bg-slate-950 pl-9 pr-3 text-[15px] text-slate-100 outline-none focus:border-soq-biru" />
        </label>
        <label className="max-w-full">
          <span className="sr-only">Kategori</span>
          <select data-testid="pl-kategori" value={kat} onChange={(e) => setKat(e.target.value)}
            className="min-h-[44px] max-w-full rounded-lg border border-slate-700 bg-slate-950 px-3 text-[15px] text-slate-100">
            <option value="">Semua kategori</option>
            {(d?.kategori || []).map((k) => <option key={k.kode} value={k.kode}>{k.nama}</option>)}
          </select>
        </label>
      </div>
      {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{err}</div>}
      {!d && !err && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Memuat…</div>}
      {d && (
        <>
          <p data-testid="pl-jumlah" className="text-sm text-slate-400">{rows.length} barang</p>
          {kecil ? (
            <div data-testid="pl-kartu" className="space-y-3">
              {rows.map((b) => (
                <div key={b.kode} data-testid={`pl-baris-${b.kode}`} className="rounded-xl border border-slate-800 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-100">{b.nama}</p>
                      <p className="font-mono text-xs text-slate-500">{b.kode} · {b.satuan || "—"}</p>
                    </div>
                    <span className="shrink-0"><Kategori b={b} /></span>
                  </div>
                  {tanpaHarga(b) ? <p className="mt-2"><SesuaiPesanan b={b} /></p> : (
                    <dl className="mt-3 grid grid-cols-3 gap-2">
                      {KOLOM_HARGA.map(([k, label]) => (
                        <div key={k} className="min-w-0 rounded-lg bg-slate-900 px-2 py-1.5">
                          <dt className="text-[11px] font-semibold text-slate-500">{label}</dt>
                          <dd className="text-sm break-words"><Harga b={b} kolom={k} besar /></dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-800 bg-white overflow-hidden">
              <table data-testid="pl-tabel" className="w-full text-sm">
                <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                  <th className="px-4 py-3">Kode</th><th className="px-4 py-3">Nama barang</th><th className="px-4 py-3">Kategori</th>
                  <th className="px-4 py-3">Satuan</th>
                  {KOLOM_HARGA.map(([k, label]) => <th key={k} className="px-4 py-3 text-right">{label}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.kode} data-testid={`pl-baris-${b.kode}`} className="border-b border-slate-800/60 align-top">
                      <td className="px-4 py-3 font-mono text-slate-400 whitespace-nowrap">{b.kode}</td>
                      <td className="px-4 py-3 text-slate-100">{b.nama}</td>
                      <td className="px-4 py-3"><Kategori b={b} /></td>
                      <td className="px-4 py-3 text-slate-300">{b.satuan || "—"}</td>
                      {tanpaHarga(b)
                        ? <td colSpan={3} className="px-4 py-3 text-right"><SesuaiPesanan b={b} /></td>
                        : KOLOM_HARGA.map(([k]) => <td key={k} className="px-4 py-3 text-right"><Harga b={b} kolom={k} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length === 0 && <p className="py-6 text-center text-slate-500">Tidak ada barang yang cocok.</p>}
        </>
      )}
    </div>
  );
}
